/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import { formatPrice, TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import { arrayGroupBy } from '../../../utils/funcoes';

import getResponseErrors from '../../../utils/getResponseErrors';
import PageInfo from '../../../components/PageInfo';
import HeaderRelatorio from '../HeaderRelatorio';

interface IRelatorio {
  PacNome: string;
  AgeCodigo: number;
  AgeInicio: Date;
  AgeInicioFormatado: string;
  CprValorPag: number;
  CprValorPagFormatado: string;
  PacCodigo: number;
  Procedimentos: string;
  ConNome: string;
}

interface IRelatorioFormatado {
  grupo: string;
  totalPago: number;
  totalPagoFormatado: string;
  dados: IRelatorio[];
}

interface ITotais {
  totalFormatado: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  PlaNumero: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const ConsumoCartao: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user, clinica } = useAuth();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorioFormatado[]>();
  const [totais, setTotais] = useState<ITotais>();
  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    PlaNumero: '',
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  const exibeTotais =
    (clinica.CliDrSaude && user.UsuTipo === TIPO_USUARIO.ADMINISTRADOR) ||
    (!clinica.CliDrSaude && user.UsuTipo <= TIPO_USUARIO.SUPERVISOR);

  const informacoesPagina = `<b>Descrição:</b> Exibe todos os procedimentos realizados em um determinado período, pelo número do cartão.
  ${
    clinica.CliDrSaude &&
    '<br /><b>Totais só são exibidos para ADMINISTRADORES</b>'
  }
  `;

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.ATENDENTE_CAIXA) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const validaDadosERetornaParametros = useCallback(async (): Promise<string> => {
    formRefBusca.current?.setErrors({});
    const schema = Yup.object().shape({
      dataInicio: Yup.date().required(),
      dataFim: Yup.date().required(),
      PlaNumero: Yup.string().required(),
    });

    const dados: IFormBusca = {
      dataInicio: formRefBusca.current?.getFieldValue('dataInicio'),
      dataFim: formRefBusca.current?.getFieldValue('dataFim'),
      PlaNumero: formRefBusca.current?.getFieldValue('PlaNumero'),
    };

    await schema.validate(dados, {
      abortEarly: false,
    });

    const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
    const dataFimSelecionada = parseISO(dados.dataFim.toString());

    const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
    const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

    setDataExibe({
      dataDiaSemana: `${format(
        dataInicioSelecionada,
        'dd/MM/yyyy',
      )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
      dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
    });

    return `dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&PlaNumero=${dados.PlaNumero}`;
  }, []);

  const handleSubmitBusca = useCallback(async () => {
    setLoading(true);
    try {
      setDadosRelatorio(undefined);

      const parametros = await validaDadosERetornaParametros();

      const response = await api.get<IRelatorio[]>(
        `/relatorios/consumoPorCartao?${parametros}`,
      );

      let totalGeral = 0;

      const dadosFormatados = response.data.map(rel => {
        return {
          ...rel,
          AgeInicioFormatado: format(
            parseISO(rel.AgeInicio.toString()),
            'dd/MM/yyyy',
          ),
          CprValorPagFormatado: formatPrice(rel.CprValorPag),
        };
      });

      const ordenado = arrayGroupBy(dadosFormatados, i => i.ConNome);

      const ordenadoFormatado = ordenado.map(ord => {
        const totalPago = ord.reduce((tot, item) => {
          return tot + Number(item.CprValorPag);
        }, 0);

        totalGeral += totalPago;

        return {
          grupo: ord[0].ConNome,
          totalPago,
          totalPagoFormatado: formatPrice(totalPago),
          dados: ord,
        };
      });

      setDadosRelatorio(ordenadoFormatado);
      setTotais({
        totalFormatado: formatPrice(totalGeral),
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      } else {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [addToast, validaDadosERetornaParametros]);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Consumo Por Cartão</h2>
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.ATENDENTE_CAIXA}
            />
          </div>

          {user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={2}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={2}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>
                <Col md={2}>
                  <Input name="PlaNumero" label="Número" required />
                </Col>
                <Col md={2}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        <HeaderRelatorio
          dataImpressao={dataExibe?.dataHora}
          titulo={`CONSUMO CARTÃO ${dataExibe?.dataDiaSemana || ''}`}
        />

        <div className="bg-white border p-2">
          {dadosRelatorio && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '80px' }}>Data</th>
                    <th>Paciente</th>
                    <th>Procedimento</th>
                    <th style={{ minWidth: '115px' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <React.Fragment key={`pro-${rel.grupo}`}>
                        <tr
                          style={{
                            fontSize: 'Small',
                            height: '30px',
                            fontWeight: 'bold',
                          }}
                        >
                          <td colSpan={exibeTotais ? 3 : 5}>
                            <div className="d-flex justify-content-between">
                              {rel.grupo}{' '}
                              <span>Nº Registros: {rel.dados.length}</span>
                            </div>
                          </td>
                          {exibeTotais && (
                            <>
                              <td>Total: {rel.totalPagoFormatado}</td>
                            </>
                          )}
                        </tr>
                        {rel.dados.map(relatorio => (
                          <tr
                            key={`proced-${rel.grupo}-${relatorio.AgeCodigo}`}
                            data-id={relatorio.AgeCodigo}
                            style={{ fontSize: '.7rem', height: '30px' }}
                          >
                            <td>{relatorio.AgeInicioFormatado}</td>
                            <td>{relatorio.PacNome}</td>
                            <td>{relatorio.Procedimentos}</td>
                            <td>{relatorio.CprValorPagFormatado}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  {exibeTotais && (
                    <tr
                      key="fdfdsfsdfdf"
                      style={{
                        fontSize: 'Small',
                        height: '30px',
                        fontWeight: 'bold',
                      }}
                    >
                      <td colSpan={4} style={{ textAlign: 'right' }}>
                        Total Geral: {totais?.totalFormatado}
                      </td>
                      {/* <td>Total Desc: {totais?.totalDescontoFormatado}</td> */}
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default ConsumoCartao;
